// hooks/useOverlayState.ts
// Custom hook for managing overlay states using useReducer

import { useReducer, Reducer } from 'react'

// ============================================================================
// State Shape (unchanged for backward compatibility)
// ============================================================================

export interface OverlayState {
  showLinkOverlay: boolean
  showHover: boolean
  showPanel: boolean
  showAccounts: boolean
  showAccountsIntro: boolean
  showContactForm: boolean
  showQA: boolean
  showXroad: boolean
  showCallMeForm: boolean
}

export interface OverlayData {
  linkUrl: string | null
  panelData: any
  contractType: string
  showPanelButton: boolean
}

// Combined state for reducer
interface CombinedOverlayState extends OverlayState, OverlayData {}

// ============================================================================
// Action Types
// ============================================================================

export enum OverlayActionType {
  // Single overlay operations
  SHOW_LINK_OVERLAY = 'SHOW_LINK_OVERLAY',
  HIDE_LINK_OVERLAY = 'HIDE_LINK_OVERLAY',
  SHOW_HOVER = 'SHOW_HOVER',
  HIDE_HOVER = 'HIDE_HOVER',
  SHOW_PANEL = 'SHOW_PANEL',
  HIDE_PANEL = 'HIDE_PANEL',
  SHOW_ACCOUNTS = 'SHOW_ACCOUNTS',
  HIDE_ACCOUNTS = 'HIDE_ACCOUNTS',
  SHOW_ACCOUNTS_INTRO = 'SHOW_ACCOUNTS_INTRO',
  HIDE_ACCOUNTS_INTRO = 'HIDE_ACCOUNTS_INTRO',
  SHOW_CONTACT_FORM = 'SHOW_CONTACT_FORM',
  HIDE_CONTACT_FORM = 'HIDE_CONTACT_FORM',
  SHOW_QA = 'SHOW_QA',
  HIDE_QA = 'HIDE_QA',
  SHOW_XROAD = 'SHOW_XROAD',
  HIDE_XROAD = 'HIDE_XROAD',
  SHOW_CALL_ME_FORM = 'SHOW_CALL_ME_FORM',
  HIDE_CALL_ME_FORM = 'HIDE_CALL_ME_FORM',

  // Mass operations
  HIDE_ALL_OVERLAYS = 'HIDE_ALL_OVERLAYS',

  // Data operations
  SET_LINK_URL = 'SET_LINK_URL',
  SET_PANEL_DATA = 'SET_PANEL_DATA',
  SET_CONTRACT_TYPE = 'SET_CONTRACT_TYPE',
  SET_SHOW_PANEL_BUTTON = 'SET_SHOW_PANEL_BUTTON',
}

// ============================================================================
// Action Interfaces
// ============================================================================

interface ShowLinkOverlayAction {
  type: OverlayActionType.SHOW_LINK_OVERLAY
}

interface HideLinkOverlayAction {
  type: OverlayActionType.HIDE_LINK_OVERLAY
}

interface ShowHoverAction {
  type: OverlayActionType.SHOW_HOVER
}

interface HideHoverAction {
  type: OverlayActionType.HIDE_HOVER
}

interface ShowPanelAction {
  type: OverlayActionType.SHOW_PANEL
}

interface HidePanelAction {
  type: OverlayActionType.HIDE_PANEL
}

interface ShowAccountsAction {
  type: OverlayActionType.SHOW_ACCOUNTS
}

interface HideAccountsAction {
  type: OverlayActionType.HIDE_ACCOUNTS
}

interface ShowAccountsIntroAction {
  type: OverlayActionType.SHOW_ACCOUNTS_INTRO
}

interface HideAccountsIntroAction {
  type: OverlayActionType.HIDE_ACCOUNTS_INTRO
}

interface ShowContactFormAction {
  type: OverlayActionType.SHOW_CONTACT_FORM
}

interface HideContactFormAction {
  type: OverlayActionType.HIDE_CONTACT_FORM
}

interface ShowQAAction {
  type: OverlayActionType.SHOW_QA
}

interface HideQAAction {
  type: OverlayActionType.HIDE_QA
}

interface ShowXroadAction {
  type: OverlayActionType.SHOW_XROAD
}

interface HideXroadAction {
  type: OverlayActionType.HIDE_XROAD
}

interface ShowCallMeFormAction {
  type: OverlayActionType.SHOW_CALL_ME_FORM
}

interface HideCallMeFormAction {
  type: OverlayActionType.HIDE_CALL_ME_FORM
}

interface HideAllOverlaysAction {
  type: OverlayActionType.HIDE_ALL_OVERLAYS
}

interface SetLinkUrlAction {
  type: OverlayActionType.SET_LINK_URL
  payload: string | null
}

interface SetPanelDataAction {
  type: OverlayActionType.SET_PANEL_DATA
  payload: any
}

interface SetContractTypeAction {
  type: OverlayActionType.SET_CONTRACT_TYPE
  payload: string
}

interface SetShowPanelButtonAction {
  type: OverlayActionType.SET_SHOW_PANEL_BUTTON
  payload: boolean
}

type OverlayAction =
  | ShowLinkOverlayAction
  | HideLinkOverlayAction
  | ShowHoverAction
  | HideHoverAction
  | ShowPanelAction
  | HidePanelAction
  | ShowAccountsAction
  | HideAccountsAction
  | ShowAccountsIntroAction
  | HideAccountsIntroAction
  | ShowContactFormAction
  | HideContactFormAction
  | ShowQAAction
  | HideQAAction
  | ShowXroadAction
  | HideXroadAction
  | ShowCallMeFormAction
  | HideCallMeFormAction
  | HideAllOverlaysAction
  | SetLinkUrlAction
  | SetPanelDataAction
  | SetContractTypeAction
  | SetShowPanelButtonAction

// ============================================================================
// Initial State
// ============================================================================

const initialState: CombinedOverlayState = {
  showLinkOverlay: false,
  showHover: false,
  showPanel: false,
  showAccounts: false,
  showAccountsIntro: false,
  showContactForm: false,
  showQA: false,
  showXroad: false,
  showCallMeForm: false,
  linkUrl: '',
  panelData: null,
  contractType: '',
  showPanelButton: false
}

// ============================================================================
// Reducer Function
// ============================================================================

const overlayReducer: Reducer<CombinedOverlayState, OverlayAction> = (state, action) => {
  switch (action.type) {
    case OverlayActionType.SHOW_LINK_OVERLAY:
      return { ...state, showLinkOverlay: true }

    case OverlayActionType.HIDE_LINK_OVERLAY:
      return { ...state, showLinkOverlay: false }

    case OverlayActionType.SHOW_HOVER:
      return { ...state, showHover: true }

    case OverlayActionType.HIDE_HOVER:
      return { ...state, showHover: false }

    case OverlayActionType.SHOW_PANEL:
      return { ...state, showPanel: true }

    case OverlayActionType.HIDE_PANEL:
      return { ...state, showPanel: false }

    case OverlayActionType.SHOW_ACCOUNTS:
      return { ...state, showAccounts: true }

    case OverlayActionType.HIDE_ACCOUNTS:
      return { ...state, showAccounts: false }

    case OverlayActionType.SHOW_ACCOUNTS_INTRO:
      return { ...state, showAccountsIntro: true }

    case OverlayActionType.HIDE_ACCOUNTS_INTRO:
      return { ...state, showAccountsIntro: false }

    case OverlayActionType.SHOW_CONTACT_FORM:
      return { ...state, showContactForm: true }

    case OverlayActionType.HIDE_CONTACT_FORM:
      return { ...state, showContactForm: false }

    case OverlayActionType.SHOW_QA:
      return { ...state, showQA: true }

    case OverlayActionType.HIDE_QA:
      return { ...state, showQA: false }

    case OverlayActionType.SHOW_XROAD:
      return { ...state, showXroad: true }

    case OverlayActionType.HIDE_XROAD:
      return { ...state, showXroad: false }

    case OverlayActionType.SHOW_CALL_ME_FORM:
      return { ...state, showCallMeForm: true }

    case OverlayActionType.HIDE_CALL_ME_FORM:
      return { ...state, showCallMeForm: false }

    case OverlayActionType.HIDE_ALL_OVERLAYS:
      return {
        ...state,
        showLinkOverlay: false,
        showHover: false,
        showPanel: false,
        showAccounts: false,
        showAccountsIntro: false,
        showContactForm: false,
        showQA: false,
        showXroad: false,
        showCallMeForm: false,
        linkUrl: null,
        showPanelButton: false
      }

    case OverlayActionType.SET_LINK_URL:
      return { ...state, linkUrl: action.payload }

    case OverlayActionType.SET_PANEL_DATA:
      return { ...state, panelData: action.payload }

    case OverlayActionType.SET_CONTRACT_TYPE:
      return { ...state, contractType: action.payload }

    case OverlayActionType.SET_SHOW_PANEL_BUTTON:
      return { ...state, showPanelButton: action.payload }

    default:
      return state
  }
}

// ============================================================================
// Action Creators (for Phase 2 modern API)
// ============================================================================

export const overlayActions = {
  showLinkOverlay: (url?: string | null) => ({
    type: OverlayActionType.SHOW_LINK_OVERLAY,
    ...(url !== undefined && { payload: url })
  }),
  hideLinkOverlay: () => ({ type: OverlayActionType.HIDE_LINK_OVERLAY }),
  showHover: () => ({ type: OverlayActionType.SHOW_HOVER }),
  hideHover: () => ({ type: OverlayActionType.HIDE_HOVER }),
  showPanel: (data?: any) => ({ type: OverlayActionType.SHOW_PANEL, ...(data && { payload: data }) }),
  hidePanel: () => ({ type: OverlayActionType.HIDE_PANEL }),
  showAccounts: () => ({ type: OverlayActionType.SHOW_ACCOUNTS }),
  hideAccounts: () => ({ type: OverlayActionType.HIDE_ACCOUNTS }),
  showAccountsIntro: () => ({ type: OverlayActionType.SHOW_ACCOUNTS_INTRO }),
  hideAccountsIntro: () => ({ type: OverlayActionType.HIDE_ACCOUNTS_INTRO }),
  showContactForm: () => ({ type: OverlayActionType.SHOW_CONTACT_FORM }),
  hideContactForm: () => ({ type: OverlayActionType.HIDE_CONTACT_FORM }),
  showQA: () => ({ type: OverlayActionType.SHOW_QA }),
  hideQA: () => ({ type: OverlayActionType.HIDE_QA }),
  showXroad: () => ({ type: OverlayActionType.SHOW_XROAD }),
  hideXroad: () => ({ type: OverlayActionType.HIDE_XROAD }),
  showCallMeForm: () => ({ type: OverlayActionType.SHOW_CALL_ME_FORM }),
  hideCallMeForm: () => ({ type: OverlayActionType.HIDE_CALL_ME_FORM }),
  hideAllOverlays: () => ({ type: OverlayActionType.HIDE_ALL_OVERLAYS }),
  setLinkUrl: (url: string | null) => ({ type: OverlayActionType.SET_LINK_URL, payload: url }),
  setPanelData: (data: any) => ({ type: OverlayActionType.SET_PANEL_DATA, payload: data }),
  setContractType: (type: string) => ({ type: OverlayActionType.SET_CONTRACT_TYPE, payload: type }),
  setShowPanelButton: (show: boolean) => ({ type: OverlayActionType.SET_SHOW_PANEL_BUTTON, payload: show })
}

// ============================================================================
// Custom Hook - Backward Compatible API
// ============================================================================

export function useOverlayState() {
  const [state, dispatch] = useReducer(overlayReducer, initialState)

  return {
    // Overlay visibility states (unchanged API)
    overlays: {
      showLinkOverlay: state.showLinkOverlay,
      showHover: state.showHover,
      showPanel: state.showPanel,
      showAccounts: state.showAccounts,
      showAccountsIntro: state.showAccountsIntro,
      showContactForm: state.showContactForm,
      showQA: state.showQA,
      showXroad: state.showXroad,
      showCallMeForm: state.showCallMeForm
    },

    // Overlay data (unchanged API)
    overlayData: {
      linkUrl: state.linkUrl,
      panelData: state.panelData,
      contractType: state.contractType,
      showPanelButton: state.showPanelButton
    },

    // Setters for overlay visibility (backward compatible - wrap dispatch)
    setShowLinkOverlay: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_LINK_OVERLAY : OverlayActionType.HIDE_LINK_OVERLAY
      }),

    setShowHover: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_HOVER : OverlayActionType.HIDE_HOVER
      }),

    setShowPanel: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_PANEL : OverlayActionType.HIDE_PANEL
      }),

    setShowAccounts: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_ACCOUNTS : OverlayActionType.HIDE_ACCOUNTS
      }),

    setShowAccountsIntro: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_ACCOUNTS_INTRO : OverlayActionType.HIDE_ACCOUNTS_INTRO
      }),

    setShowContactForm: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_CONTACT_FORM : OverlayActionType.HIDE_CONTACT_FORM
      }),

    setShowQA: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_QA : OverlayActionType.HIDE_QA
      }),

    setShowXroad: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_XROAD : OverlayActionType.HIDE_XROAD
      }),

    setShowCallMeForm: (show: boolean) =>
      dispatch({
        type: show ? OverlayActionType.SHOW_CALL_ME_FORM : OverlayActionType.HIDE_CALL_ME_FORM
      }),

    // Setters for overlay data (backward compatible - wrap dispatch)
    setLinkUrl: (url: string | null) =>
      dispatch({ type: OverlayActionType.SET_LINK_URL, payload: url }),

    setPanelData: (data: any) =>
      dispatch({ type: OverlayActionType.SET_PANEL_DATA, payload: data }),

    setContractType: (type: string) =>
      dispatch({ type: OverlayActionType.SET_CONTRACT_TYPE, payload: type }),

    setShowPanelButton: (show: boolean) =>
      dispatch({ type: OverlayActionType.SET_SHOW_PANEL_BUTTON, payload: show }),

    // Utility (unchanged API)
    hideAllOverlays: () =>
      dispatch({ type: OverlayActionType.HIDE_ALL_OVERLAYS }),

    // NEW: Expose dispatch for future Phase 2 migration
    dispatch
  }
}
